import { useTranslation } from 'react-i18next';

import { InputNumber } from '@arco-design/web-react';
import { IconRefresh } from '@arco-design/web-react/icon';
import { useState } from 'react';
import styled from 'styled-components';
import type { UseCaptchaReturn } from './useCaptcha';
import { useCaptcha } from './useCaptcha';

/**
 * 验证码组件的 Props 接口
 */
interface CaptchaInputProps {
  /** Canvas ref,来自 useCaptcha hook */
  canvasRef: React.RefObject<HTMLCanvasElement>;
  /** 用户输入的值,来自 useCaptcha hook */
  inputValue: number;
  /** 设置用户输入值的函数,来自 useCaptcha hook */
  setInputValue: (value: number) => void;
  /** 验证结果,来自 useCaptcha hook */
  isValid: boolean;
  /** 刷新验证码的函数,来自 useCaptcha hook */
  refresh: () => void;
  /** 验证码输入框的占位符文本 */
  placeholder?: string;
  /** 错误提示文本 */
  errorMessage?: string;
  /** 是否在失去焦点时显示错误 */
  showErrorOnBlur?: boolean;
}

/**
 * 验证码输入组件(纯展示组件)
 * 功能:通过 canvas 展示两个随机整数相加的算术题,用户需要输入正确答案
 * 注意:需要配合 useCaptcha hook 使用,所有状态和方法由外部传入
 */
function CaptchaInput(props: CaptchaInputProps) {
  const { t } = useTranslation();
  const {
    canvasRef,
    inputValue,
    setInputValue,
    isValid,
    refresh,
    placeholder = t('common:please.enter.the.verification.code'),
    errorMessage = t('components:incorrect.verification.code'),
    showErrorOnBlur = true,
  } = props;

  // 记录是否已失去焦点(用于判断是否显示错误)
  const [hasBlurred, setHasBlurred] = useState(false);

  // 判断是否显示错误信息
  const shouldShowError = () => {
    // 如果没有输入内容,不显示错误
    if (!inputValue) return false;

    // 根据配置决定何时显示错误
    switch (true) {
      case showErrorOnBlur && hasBlurred:
        // 失去焦点后才显示错误
        return !isValid;
      case !showErrorOnBlur:
        // 实时显示错误
        return !isValid;
      default:
        return false;
    }
  };

  const showError = shouldShowError();

  return (
    <CaptchaContainer>
      <CaptchaWrapper $hasError={showError}>
        <CanvasWrapper>
          <canvas ref={canvasRef} width={150} height={32} />
          <RefreshButton
            type="button"
            onClick={refresh}
            title={t('components:refresh.verification.code')}
          >
            <IconRefresh />
          </RefreshButton>
        </CanvasWrapper>
        <InputNumber
          type="number"
          value={inputValue}
          onChange={setInputValue}
          onBlur={() => setHasBlurred(true)}
          onFocus={() => setHasBlurred(false)}
          placeholder={placeholder}
          autoComplete="off"
          style={{ flex: 1 }}
        />
      </CaptchaWrapper>
      {/* 错误提示信息 */}
      {showError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </CaptchaContainer>
  );
}

export default CaptchaInput;
export { useCaptcha };
export type { CaptchaInputProps, UseCaptchaReturn };

// ============ Styled Components ============

const CaptchaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CaptchaWrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CanvasWrapper = styled.div`
  position: relative;
  display: inline-block;
  border: 1px solid var(--color-border-2, #e5e6eb);
  border-radius: 4px;
  overflow: hidden;
  width: fit-content;

  canvas {
    display: block;
    cursor: pointer;
  }
`;

const RefreshButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    transform: rotate(180deg);
  }

  &:active {
    transform: rotate(180deg);
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-danger, #f53f3f);
  font-size: 12px;
  line-height: 1.5;
  padding-left: 2px;
`;
